import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AnalyticsResponse } from '../types';
import { ApiError, api } from '../api/client';
import { Button, Card, Notice, Spinner } from '../components/ui/primitives';
import {
  BarList,
  ChartCard,
  Meter,
  StatTile,
  TrendChart,
  formatAudience,
} from '../components/dashboard/charts';

/**
 * Home — the dashboard.
 *
 * Answers the questions someone opens the application to ask: how much is in
 * flight, how much is actually live in the channels, and what is still waiting.
 * Everything is derived from the rollout records by the server, so no figure here
 * can drift from the requests it describes.
 *
 * The numbers lead and the charts support them. A count is a stat tile rather
 * than a one-bar chart, and every chart carries a single series — which keeps it
 * legible and avoids inventing colours to tell series apart.
 */

/** `2026-08` → `Aug`, in the reader's locale. */
function monthLabel(month: string): string {
  const [year, index] = month.split('-').map(Number);
  if (!year || !index) return month;

  return new Date(Date.UTC(year, index - 1, 1)).toLocaleDateString(undefined, { month: 'short' });
}

function formatDate(iso: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

/** How long a request has been waiting, in whole days. */
function waitingDays(iso: string): number {
  const created = new Date(iso).getTime();
  if (Number.isNaN(created)) return 0;
  return Math.max(0, Math.floor((Date.now() - created) / 86_400_000));
}

export function DashboardScreen({ onOpenRequests }: { onOpenRequests: () => void }) {
  const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showTable, setShowTable] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setAnalytics(await api.getAnalytics());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The dashboard could not be loaded.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const trend = useMemo(
    () =>
      (analytics?.byMonth ?? []).map((entry) => ({
        label: monthLabel(entry.month),
        value: entry.requests,
      })),
    [analytics],
  );

  if (error) {
    return (
      <Notice
        tone="error"
        title="Could not load the dashboard"
        action={
          <Button variant="secondary" onClick={() => void load()} className="px-4 py-1.5">
            Try again
          </Button>
        }
      >
        {error}
      </Notice>
    );
  }

  if (!analytics) {
    return (
      <Card className="p-8">
        <div className="flex items-center justify-center gap-2.5 text-field text-ink-muted">
          <Spinner />
          Loading the dashboard…
        </div>
      </Card>
    );
  }

  const { totals, byChannel, topBrands, workflow, outstanding } = analytics;

  if (!totals.requests) {
    return (
      <Card className="p-10">
        <div className="text-center">
          <p className="text-heading font-semibold text-ink">Nothing to report yet</p>
          <p className="mx-auto mt-1.5 max-w-md text-field text-ink-muted">
            Once a rollout has been created, this page shows what is in flight, what is live in the
            channels, and what is still waiting to be pushed.
          </p>
          <div className="mt-5">
            <Button onClick={onOpenRequests}>Create the first request</Button>
          </div>
        </div>
      </Card>
    );
  }

  const coverage = totals.cohorts ? Math.round((totals.cohortsPushed / totals.cohorts) * 100) : 0;

  return (
    <div className="space-y-5">
      {/* The headline numbers, as tiles rather than charts. */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatTile
          label="Requests"
          value={totals.requests}
          hint={`${totals.combinations} brand and agency combination${totals.combinations === 1 ? '' : 's'}`}
        />
        <StatTile
          label="Audience pushed"
          value={formatAudience(totals.audiencePushed)}
          hint={`of ${formatAudience(totals.audienceRequested)} requested`}
        />
        <StatTile
          label="Cohorts covered"
          value={`${coverage}%`}
          hint={`${totals.cohortsPushed} of ${totals.cohorts}, ${totals.pushes} push${
            totals.pushes === 1 ? '' : 'es'
          } recorded`}
        />
        <StatTile
          label="Still to push"
          value={totals.outstanding}
          hint={
            totals.outstanding
              ? `request${totals.outstanding === 1 ? '' : 's'} with cohorts waiting`
              : 'every request is complete'
          }
          tone={totals.outstanding ? 'attention' : 'neutral'}
        />
      </div>

      <ChartCard
        title="Push coverage"
        hint="Cohorts with at least one audience live in the channel."
        aside={
          <span className="text-micro text-ink-muted">
            {workflow.agency} via agency · {workflow.direct} direct
          </span>
        }
      >
        <Meter value={totals.cohortsPushed} total={totals.cohorts} label="Cohorts pushed" />
      </ChartCard>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ChartCard title="Requests by channel" hint="Tabs created, per channel.">
          <BarList
            data={byChannel.map((entry) => ({
              label: entry.channel,
              value: entry.requests,
              sublabel: `${entry.cohortsPushed}/${entry.cohorts} cohorts`,
            }))}
          />
        </ChartCard>

        <ChartCard
          title="Audience pushed by platform"
          hint="People live in each channel, taking the upper bound of every recorded size."
          aside={
            totals.audienceAssumed ? (
              <span className="text-micro text-ink-faint">
                {totals.audienceAssumed} size{totals.audienceAssumed === 1 ? '' : 's'} read as
                millions
              </span>
            ) : null
          }
        >
          <BarList
            data={byChannel
              .filter((entry) => entry.audiencePushed > 0)
              .sort((a, b) => b.audiencePushed - a.audiencePushed)
              .map((entry) => ({
                label: entry.channel,
                value: entry.audiencePushed,
                display: formatAudience(entry.audiencePushed),
                sublabel: `of ${formatAudience(entry.audienceRequested)}`,
              }))}
          />
        </ChartCard>

        <ChartCard title="Most active brands" hint="Requests raised, per brand.">
          <BarList
            data={topBrands.map((entry) => ({
              label: entry.brandName,
              value: entry.requests,
              sublabel: entry.businessType === 'AGENCY' ? entry.agencyName : 'Direct',
            }))}
          />
        </ChartCard>
      </div>

      <ChartCard
        title="Requests over time"
        hint="The last six months, including any with none."
        aside={
          <button
            type="button"
            onClick={() => setShowTable((current) => !current)}
            className="text-micro text-ink-muted underline decoration-line-strong hover:text-ink"
          >
            {showTable ? 'Hide the numbers' : 'Show the numbers'}
          </button>
        }
      >
        <TrendChart points={trend} caption="Requests raised per month over the last six months" />

        {/*
          The table is the accessible counterpart to the plot, and the fallback for
          anyone who cannot read the mark colour — a chart should never be the only
          way to reach its data.
        */}
        {showTable ? (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full border-collapse text-left text-field">
              <thead>
                <tr className="border-b border-line text-micro uppercase tracking-wide text-ink-muted">
                  <th scope="col" className="py-2 pr-4 font-semibold">
                    Month
                  </th>
                  <th scope="col" className="py-2 pr-4 font-semibold">
                    Requests
                  </th>
                  <th scope="col" className="py-2 font-semibold">
                    Cohorts pushed
                  </th>
                </tr>
              </thead>
              <tbody>
                {analytics.byMonth.map((entry) => (
                  <tr key={entry.month} className="border-b border-line-soft last:border-0">
                    <td className="py-2 pr-4 text-ink-muted">{entry.month}</td>
                    <td className="py-2 pr-4 tabular-nums text-ink">{entry.requests}</td>
                    <td className="py-2 tabular-nums text-ink">{entry.cohortsPushed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </ChartCard>

      {outstanding.length ? (
        <ChartCard
          title="Waiting the longest"
          hint="Requests with cohorts that have not been pushed."
          aside={
            <Button variant="secondary" className="px-3 py-1.5" onClick={onOpenRequests}>
              Go to requests
            </Button>
          }
        >
          <ul className="divide-y divide-line-soft">
            {outstanding.map((entry) => (
              <li key={entry.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-field font-medium text-ink">
                    {entry.brandName}
                    <span className="ml-2 font-normal text-ink-muted">{entry.tabTitle}</span>
                  </p>
                  <p className="mt-0.5 text-micro text-ink-faint">
                    {entry.agencyName || 'Direct'} · {entry.channel} · created{' '}
                    {formatDate(entry.createdAt)}
                  </p>
                </div>
                <p className="shrink-0 text-micro text-ink-muted">
                  {entry.cohortsPushed}/{entry.cohorts} pushed
                  <span className="ml-2 text-ink-faint">
                    {waitingDays(entry.createdAt)} day
                    {waitingDays(entry.createdAt) === 1 ? '' : 's'} old
                  </span>
                </p>
              </li>
            ))}
          </ul>
        </ChartCard>
      ) : null}
    </div>
  );
}
